import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// ---------------------------------------------------------------------------
// Employment (CIM/B2B) API client — Echipa: Angajare.
//
// Mirrors the backend contract in backend/src/routes/people.ts (employment
// section). Reads are gated on people.canViewRates, writes on people.canEdit.
// ---------------------------------------------------------------------------

export type EmploymentType = 'CONTRACTOR' | 'EMPLOYEE';

export interface PersonEmployment {
  type: EmploymentType | null;
  companyName: string | null;
  companyCui: string | null;
  companyRegCom: string | null;
  companyAddress: string | null;
  iban: string | null;
  hasCim: boolean;
  cimFileName: string | null;
  cimMimeType: string | null;
  cimFileSize: number | null;
  cimUploadedAt: string | null;
}

export interface SaveEmploymentInput {
  type: EmploymentType;
  companyName?: string | null;
  companyCui?: string | null;
  companyRegCom?: string | null;
  companyAddress?: string | null;
  iban?: string | null;
}

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// GET /api/people/:id/employment
export async function getEmployment(personId: string): Promise<PersonEmployment | null> {
  const res = await apiFetch<Envelope<{ employment: PersonEmployment | null }>>(
    `/api/people/${personId}/employment`,
    { headers: authHeaders() },
  );
  return res.data.employment;
}

// PUT /api/people/:id/employment
export async function saveEmployment(
  personId: string,
  input: SaveEmploymentInput,
): Promise<PersonEmployment> {
  const res = await apiFetch<Envelope<{ employment: PersonEmployment }>>(
    `/api/people/${personId}/employment`,
    { method: 'PUT', headers: authHeaders(), body: JSON.stringify(input) },
  );
  return res.data.employment;
}

// POST /api/people/:id/employment/cim — raw-body upload (filename in query).
export async function uploadCim(personId: string, file: File): Promise<PersonEmployment> {
  const query = new URLSearchParams({ name: file.name });
  const res = await apiFetch<Envelope<{ employment: PersonEmployment }>>(
    `/api/people/${personId}/employment/cim?${query.toString()}`,
    {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': file.type || 'application/octet-stream',
      },
      body: file,
    },
  );
  return res.data.employment;
}

// GET /api/people/:id/employment/cim/preview — signed view/download URL.
export async function getCimPreviewUrl(
  personId: string,
): Promise<{ url: string; mimeType: string | null; name: string | null }> {
  const res = await apiFetch<
    Envelope<{ url: string; mimeType: string | null; name: string | null }>
  >(`/api/people/${personId}/employment/cim/preview`, { headers: authHeaders() });
  return res.data;
}

// DELETE /api/people/:id/employment/cim
export async function deleteCim(personId: string): Promise<PersonEmployment> {
  const res = await apiFetch<Envelope<{ employment: PersonEmployment }>>(
    `/api/people/${personId}/employment/cim`,
    { method: 'DELETE', headers: authHeaders() },
  );
  return res.data.employment;
}
